import { isRecord } from 'utils/isRecord/isRecord';
import { Optional } from 'utils/utilityTypes';

export const ADD_REQUEST = 'FAVOURITE_CLIENT/ADD_TO_FAVOURITES';
export const ADD_LOADING = 'FAVOURITE_CLIENT/ADD_TO_FAVOURITES_INITIATE';
export const ADD_SUCCESS = 'FAVOURITE_CLIENT/ADD_TO_FAVOURITES_SUCCESS';
export const ADD_FAILURE = 'FAVOURITE_CLIENT/ADD_TO_FAVOURITES_FAIL';

// Save for later events
export const SAVE_FOR_LATER_REQUEST = 'FAVOURITE_CLIENT/SAVE_ITEMS_FOR_LATER';
export const SAVE_FOR_LATER_LOADING =
    'FAVOURITE_CLIENT/SAVE_ITEMS_FOR_LATER_LOADING';
export const SAVE_FOR_LATER_SUCCESS =
    'FAVOURITE_CLIENT/SAVE_ITEMS_FOR_LATER_SUCCESS';
export const SAVE_FOR_LATER_FAILURE =
    'FAVOURITE_CLIENT/SAVE_ITEMS_FOR_LATER_FAILURE';
export const SAVE_FOR_LATER_REMOVE_REQUEST =
    'FAVOURITE_CLIENT/SAVED_FOR_LATER_REMOVE_ITEM';
export const SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST =
    'FAVOURITE_CLIENT/SAVED_FOR_LATER_REMOVE_ITEMS';
export const SAVE_FOR_LATER_REMOVE_ITEMS_INITIATE =
    'FAVOURITE_CLIENT/SAVED_FOR_LATER_REMOVE_ITEMS_INITIATE';
export const SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST_SUCCESS =
    'FAVOURITE_CLIENT/SAVED_FOR_LATER_REMOVE_ITEMS_SUCCESS';
export const SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST_FAIL =
    'FAVOURITE_CLIENT/SAVED_FOR_LATER_REMOVE_ITEMS_FAIL';

// Events that allow to add multiple items to Favourites
export const ADD_ITEMS_REQUEST = 'FAVOURITE_CLIENT/ADD_ITEMS_TO_FAVOURITES';
export const ADD_ITEMS_LOADING =
    'FAVOURITE_CLIENT/ADD_ITEMS_TO_FAVOURITES_INITIATE';
export const ADD_ITEMS_SUCCESS =
    'FAVOURITE_CLIENT/ADD_ITEMS_TO_FAVOURITES_SUCCESS';
export const ADD_ITEMS_FAILURE =
    'FAVOURITE_CLIENT/ADD_ITEMS_TO_FAVOURITES_FAIL';
export const ADD_ITEMS_ABORTED =
    'FAVOURITE_CLIENT/ADD_ITEMS_TO_FAVOURITES_ABORTED';

export const REMOVE_REQUEST = 'FAVOURITE_CLIENT/REMOVE_FROM_FAVOURITES';
export const REMOVE_LOADING =
    'FAVOURITE_CLIENT/REMOVE_FROM_FAVOURITES_INITIATE';
export const REMOVE_SUCCESS = 'FAVOURITE_CLIENT/REMOVE_FROM_FAVOURITES_SUCCESS';
export const REMOVE_FAILURE = 'FAVOURITE_CLIENT/REMOVE_FROM_FAVOURITES_FAIL';

export const UPDATE_FAVOURITES = 'FAVOURITE_CLIENT/FAVOURITES_UPDATED';

export const AGENT_LOADED = 'FAVOURITE_CLIENT/AGENT_LOADED';

// Events triggered from Cart and Favourites when lists are updated
export const UPDATE_FAVOURITES_DATA = 'FAVOURITE_CLIENT/UPDATE_DATA';
export const MERGE_SAVED_FOR_LATER = 'FAVOURITE_CLIENT/MERGE_SAVED_FOR_LATER';

export type FavouriteItemPayload = {
    productName: string;
    productNumber: string;
    productType: string;
    quantity?: string;
    design?: string;
};

export type DesignPayload = {
    configurationId: string;
    quantity: number;
};

type CommonAttributes = {
    clientId?: string;
    source: string;
    storeMode?: boolean;
    skipSavedForLaterToast?: boolean;
    ga?: {
        custom: any;
    };
};

export type SingleItemPayload = FavouriteItemPayload & CommonAttributes;

export type MultipleItemPayload = {
    items: FavouriteItemPayload[];
    designs?: DesignPayload[];
} & CommonAttributes;

export type RemoveFromListPayload = Optional<
    FavouriteItemPayload,
    'productType' | 'quantity'
> &
    CommonAttributes;

export type FavouritesPayload = SingleItemPayload | MultipleItemPayload;

export type FavouritesEvent = IkeaEvent<FavouritesPayload>;

export const publish = window?.ikea?.pubsub?.publish;
export const subscribe = window?.ikea?.pubsub?.subscribe;

export const isMultipleItemsPayload = (
    arg: unknown
): arg is MultipleItemPayload => {
    if (!isRecord(arg)) return false;
    if (
        !(Array.isArray(arg?.items) && arg?.items.every(isFavouriteItemPayload))
    ) {
        return false;
    }
    return isCommonAttributes(arg);
};

const isCommonAttributes = (arg: unknown): arg is CommonAttributes => {
    if (!isRecord(arg)) return false;
    if (typeof arg?.source !== 'string') {
        console.warn(`favs-agent: missing required prop "source"`);
        return false;
    }
    return true;
};

const isFavouriteItemPayload = (arg: unknown): arg is FavouriteItemPayload => {
    if (!isRecord(arg)) return false;
    if (typeof arg?.productName !== 'string') {
        console.warn(`favs-agent: missing required prop "productName"`);
        return false;
    }
    // Don't validate the rest of the fields if the payload contains a design code
    if (typeof arg?.design === 'string') {
        return true;
    }
    if (typeof arg?.productNumber !== 'string') {
        console.warn(`favs-agent: missing required prop "productNumber"`);
        return false;
    }
    if (typeof arg?.productType !== 'string') {
        console.warn(`favs-agent: missing required prop "productType"`);
        return false;
    }
    if (arg?.quantity && typeof arg?.quantity !== 'string') {
        console.warn(`favs-agent: "quantity" prop should be of type string`);
    }
    return true;
};

export const isRemoveFromListPayload = (
    arg: unknown
): arg is RemoveFromListPayload => {
    if (!isRecord(arg)) return false;
    if (typeof arg?.productName !== 'string') {
        console.warn(`favs-agent: missing required prop "productName"`);
        return false;
    }
    // Don't validate the rest of the fields if the payload contains a design code
    if (typeof arg?.design === 'string') {
        return isCommonAttributes(arg);
    }
    if (typeof arg?.productNumber !== 'string') {
        console.warn(`favs-agent: missing required prop "productNumber"`);
        return false;
    }
    if (arg?.quantity && typeof arg?.quantity !== 'string') {
        console.warn(`favs-agent: "quantity" prop should be of type string`);
    }
    return isCommonAttributes(arg);
};

export const isSingleItemPayload = (arg: unknown): arg is SingleItemPayload => {
    if (!isRecord(arg)) {
        return false;
    }
    if (Array.isArray(arg?.items)) {
        return false;
    }
    return isFavouriteItemPayload(arg) && isCommonAttributes(arg);
};

export const isFavouritesPayload = (arg: unknown): arg is FavouritesPayload => {
    if (!isRecord(arg)) return false;

    if (Array.isArray(arg?.items)) {
        return isMultipleItemsPayload(arg);
    }

    return isSingleItemPayload(arg);
};
